/**
 * Notification Utilities
 *
 * Framework-agnostic utilities for parsing, formatting, and handling notifications.
 */

import { dayjs } from './date';
import { NOTIFICATION_CONSTANTS } from '../config/constants';

import type { DateInput } from './date';
import type {
  NotificationAction,
  NotificationCategory,
  NotificationData,
  NotificationEnvelope,
  NotificationItem,
  NotificationKind,
  NotificationPriority,
  RawNotification,
} from '../types/notification.types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Every category with an inbox tab, in the order the inbox shows them. The
 * API's `account` notices have none: rare security notices, they show under
 * All.
 */
export const NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  'achievement',
  'challenge',
  'social',
  'coaching',
  'report',
  'training',
] as const;

/**
 * The account notices' kinds: a way in added or removed, and the email
 * changed. A push carries its kind but not its category, so a tapped one is
 * known by these.
 */
export const ACCOUNT_NOTIFICATION_KINDS: readonly NotificationKind[] = [
  'sign_in_method_added',
  'sign_in_method_removed',
  'email_changed',
];

/** Whether a kind is an account notice's: the account changed elsewhere. */
export function isAccountNotificationKind(kind: string | undefined): boolean {
  return ACCOUNT_NOTIFICATION_KINDS.some((accountKind) => accountKind === kind);
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard for raw notifications from Laravel Echo
 */
export function isRawNotification(data: unknown): data is RawNotification {
  if (typeof data !== 'object' || data === null) return false;

  const obj = data as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.type === 'string' &&
    typeof obj.data === 'object' &&
    obj.data !== null &&
    (obj.read_at === null || typeof obj.read_at === 'string') &&
    typeof obj.created_at === 'string' &&
    typeof obj.updated_at === 'string'
  );
}

/**
 * Type guard for Laravel broadcast notification event payload
 */
export function isLaravelBroadcastPayload(
  data: unknown
): data is { notification: unknown } {
  return typeof data === 'object' && data !== null && 'notification' in data;
}

/**
 * Safely extract and validate notification from broadcast payload
 */
export function extractNotificationFromPayload(
  payload: unknown
): RawNotification | null {
  // Handle direct notification payload
  if (isRawNotification(payload)) {
    return payload;
  }

  // Handle Laravel broadcast wrapper: { notification: RawNotification }
  if (isLaravelBroadcastPayload(payload)) {
    const { notification } = payload;
    if (isRawNotification(notification)) {
      return notification;
    }
  }

  // Handle flattened Laravel notification payload: { id, type, ...data }
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'id' in payload &&
    'type' in payload
  ) {
    const payloadRecord = payload as Record<string, unknown>;
    const { id, type, ...data } = payloadRecord;

    if (typeof id === 'string' && typeof type === 'string') {
      const now = dayjs().toISOString();
      return {
        id,
        type,
        data: data as unknown as NotificationData,
        read_at: null,
        created_at: now,
        updated_at: now,
      };
    }
  }

  return null;
}

/**
 * Type guard for the envelope.
 *
 * `kind` is not checked against the known list on purpose. A kind this build
 * has never heard of still has a title, a message and an action, which is
 * everything a generic row needs.
 */
export function isNotificationEnvelope(
  data: unknown
): data is NotificationEnvelope {
  if (typeof data !== 'object' || data === null) return false;

  const obj = data as Record<string, unknown>;
  return (
    typeof obj.kind === 'string' &&
    typeof obj.category === 'string' &&
    typeof obj.level === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.message === 'string' &&
    'actor' in obj &&
    'action' in obj &&
    'meta' in obj
  );
}

// =============================================================================
// PARSING FUNCTIONS
// =============================================================================

/**
 * What stands in for a row that is not an envelope — one written before the API
 * adopted it, which only a development database still holds. It keeps whatever
 * `message` the row had, so the inbox degrades to a plain line instead of
 * throwing on a missing key.
 */
function fallbackEnvelope(data: unknown): NotificationEnvelope {
  const message =
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { message?: unknown }).message === 'string'
      ? (data as { message: string }).message
      : 'You have a new notification.';

  return {
    kind: 'unknown',
    category: 'training',
    level: 'info',
    title: 'Notification',
    message,
    actor: null,
    action: null,
    meta: null,
  };
}

/**
 * The notification's envelope. Every reader below goes through this, so none
 * of them has to ask whether the keys are there.
 */
export function getEnvelope(
  notification: NotificationItem | RawNotification
): NotificationEnvelope {
  return isNotificationEnvelope(notification.data)
    ? notification.data
    : fallbackEnvelope(notification.data);
}

/**
 * The stable name of what happened — what to switch on. Never the Laravel
 * class name in `notification.type`, which is free to be renamed.
 */
export function getKind(
  notification: NotificationItem | RawNotification
): string {
  return getEnvelope(notification).kind;
}

/**
 * Parse notification channel name to extract username
 */
export function parseNotificationChannel(channel: string): {
  username: string | null;
  type: string;
} {
  // Handle Laravel's default private channel format: "private-App.Models.User.{id}"
  // Or custom format: "private-users.{username}"

  const privatePrefix = 'private-';
  const actualChannel = channel.startsWith(privatePrefix)
    ? channel.substring(privatePrefix.length)
    : channel;

  // Match users.{username} pattern
  const userMatch = actualChannel.match(/^users\.(.+)$/);
  if (userMatch) {
    return { username: userMatch[1], type: 'user' };
  }

  // Match App.Models.User.{id} pattern
  const modelMatch = actualChannel.match(/^App\.Models\.User\.(\d+)$/);
  if (modelMatch) {
    return { username: null, type: 'user_id' }; // We have ID, not username
  }

  return { username: null, type: 'unknown' };
}

/**
 * Extract notification metadata
 */
export function extractNotificationMetadata(
  notification: NotificationItem | RawNotification
): {
  kind: string;
  priority: NotificationPriority;
  category: NotificationCategory;
  isUnread: boolean;
  age: number;
  ageInHours: number;
  ageInDays: number;
} {
  const kind = getKind(notification);
  const priority = getPriority(notification);
  const category = getCategory(notification);
  const isUnread = notification.read_at === null;
  const age = dayjs().diff(dayjs(notification.created_at));

  return {
    kind,
    priority,
    category,
    isUnread,
    age,
    ageInHours: Math.floor(age / (1000 * 60 * 60)),
    ageInDays: Math.floor(age / (1000 * 60 * 60 * 24)),
  };
}

// =============================================================================
// FORMATTING FUNCTIONS
// =============================================================================

/**
 * The notification's title. Written by the API; nothing is composed here.
 */
export function formatNotificationTitle(
  notification: NotificationItem | RawNotification
): string {
  return getEnvelope(notification).title;
}

/**
 * The notification's message — always a complete sentence, as sent.
 */
export function formatNotificationMessage(
  notification: NotificationItem | RawNotification
): string {
  return getEnvelope(notification).message;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * How loudly a notification should arrive. Derived from the envelope rather
 * than listed per kind, so a kind this build has never seen is still ranked:
 * a failure first, then a question someone is waiting on or a success worth
 * celebrating, then anything from a person, then the rest.
 */
export function getPriority(
  notification: NotificationItem | RawNotification
): NotificationPriority {
  const { level, category, action, actor } = getEnvelope(notification);

  if (level === 'error') return 'error';
  if (action?.type === 'respond') return 'high';
  if (category === 'achievement' || level === 'warning') return 'high';
  if (actor !== null || category === 'challenge') return 'medium';
  return 'low';
}

/**
 * Get notification category
 */
export function getCategory(
  notification: NotificationItem | RawNotification
): NotificationCategory {
  return getEnvelope(notification).category;
}

/**
 * The notification's action, or null.
 */
export function getAction(
  notification: NotificationItem | RawNotification
): NotificationAction | null {
  return getEnvelope(notification).action;
}

/**
 * Does the notification ask a question answered in place (Accept / Decline)?
 */
export function isActionable(
  notification: NotificationItem | RawNotification
): boolean {
  return getAction(notification)?.type === 'respond';
}

/**
 * Where a click on the notification itself goes: the in-app path of a
 * `navigate` action, or the file of a `download`. Null for the actions that
 * are buttons rather than destinations (`generate`, `respond`) and when there
 * is no action.
 *
 * A path is only ever relative — the API refuses to build anything else — but
 * it is checked here too, since this string is handed to a router and, from a
 * browser notification, to `window.location`.
 */
export function getNotificationAction(
  notification: NotificationItem | RawNotification
): string | null {
  const action = getAction(notification);

  if (action?.type === 'navigate') {
    return action.path.startsWith('/') && !action.path.startsWith('//')
      ? action.path
      : null;
  }

  if (action?.type === 'download') return action.url;

  return null;
}

/**
 * An API-built request path, made relative to the API client.
 *
 * The API writes whole paths ("/api/share/abc") because it cannot know how a
 * client is configured; the client's base URL already ends in `/api`, so the
 * prefix is dropped rather than doubled.
 */
export function toClientPath(apiPath: string): string {
  return apiPath.replace(/^\/api(?=\/)/, '');
}

/**
 * Group notifications by category
 */
export function groupNotificationsByCategory(
  notifications: NotificationItem[]
): Record<string, NotificationItem[]> {
  const groups: Record<string, NotificationItem[]> = {};

  notifications.forEach((notification) => {
    const category = getCategory(notification);

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(notification);
  });

  return groups;
}

/**
 * Sort notifications by priority and recency
 */
export function sortNotifications(
  notifications: NotificationItem[]
): NotificationItem[] {
  const priorityOrder = { high: 4, medium: 3, low: 2, error: 5 };

  return [...notifications].sort((a, b) => {
    // First sort by read status (unread first)
    if (a.read_at === null && b.read_at !== null) return -1;
    if (a.read_at !== null && b.read_at === null) return 1;

    // Then by priority
    const aPriority = getPriority(a);
    const bPriority = getPriority(b);
    const priorityDiff = priorityOrder[bPriority] - priorityOrder[aPriority];

    if (priorityDiff !== 0) return priorityDiff;

    // Finally by recency
    return dayjs(b.created_at).diff(dayjs(a.created_at), 'milliseconds');
  });
}

/**
 * Filter notifications by criteria
 */
export function filterNotifications(
  notifications: NotificationItem[],
  criteria: {
    unreadOnly?: boolean;
    categories?: string[];
    priorities?: string[];
    since?: DateInput;
    types?: string[];
  }
): NotificationItem[] {
  return notifications.filter((notification) => {
    const kind = getKind(notification);
    const category = getCategory(notification);
    const priority = getPriority(notification);

    // Filter by read status
    if (criteria.unreadOnly && notification.read_at !== null) {
      return false;
    }

    // Filter by categories
    if (criteria.categories && !criteria.categories.includes(category)) {
      return false;
    }

    // Filter by priorities
    if (criteria.priorities && !criteria.priorities.includes(priority)) {
      return false;
    }

    // Filter by date
    if (criteria.since) {
      if (dayjs(notification.created_at).isBefore(dayjs(criteria.since))) {
        return false;
      }
    }

    // Filter by types
    if (criteria.types && !criteria.types.includes(kind)) {
      return false;
    }

    return true;
  });
}

/**
 * Create notification summary stats
 */
export function createNotificationSummary(notifications: NotificationItem[]): {
  total: number;
  unread: number;
  categories: Record<string, number>;
  priorities: {
    high: number;
    medium: number;
    low: number;
    error: number;
  };
  hasErrors: boolean;
  hasHighPriority: boolean;
} {
  const unread = notifications.filter((n) => n.read_at === null);
  const categories = groupNotificationsByCategory(notifications);
  const priorities = {
    high: notifications.filter((n) => getPriority(n) === 'high').length,
    medium: notifications.filter((n) => getPriority(n) === 'medium').length,
    low: notifications.filter((n) => getPriority(n) === 'low').length,
    error: notifications.filter((n) => getPriority(n) === 'error').length,
  };

  return {
    total: notifications.length,
    unread: unread.length,
    categories: Object.keys(categories).reduce(
      (acc, key) => {
        acc[key] = categories[key].length;
        return acc;
      },
      {} as Record<string, number>
    ),
    priorities,
    hasErrors: priorities.error > 0,
    hasHighPriority: priorities.high > 0,
  };
}

// =============================================================================
// CHANNEL UTILITIES
// =============================================================================

/**
 * Create user notification channel name using configured format
 */
export function createUserNotificationChannel(username: string): string {
  return NOTIFICATION_CONSTANTS.CUSTOM_USER_PRIVATE_CHANNEL.replace(
    'username',
    username
  );
}

/**
 * Create Laravel user notification channel name
 */
export function createLaravelUserNotificationChannel(userId: string): string {
  return NOTIFICATION_CONSTANTS.LARAVEL_USER_PRIVATE_CHANNEL.replace(
    'id',
    userId
  );
}

/**
 * Check if notifications are enabled via kill switch
 */
export function areNotificationsEnabled(): boolean {
  return NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS;
}

/**
 * Get notification channel configuration
 */
export function getNotificationChannelConfig(): {
  customUserChannel: string;
  laravelUserChannel: string;
  enabled: boolean;
} {
  return {
    customUserChannel: NOTIFICATION_CONSTANTS.CUSTOM_USER_PRIVATE_CHANNEL,
    laravelUserChannel: NOTIFICATION_CONSTANTS.LARAVEL_USER_PRIVATE_CHANNEL,
    enabled: NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS,
  };
}
